import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const EMPTY_FORM = {
  nome: "", codigo: "", preco: "", ncm: "", unidade: "UN",
  tipo: "S", situacao: "A", descricaoCurta: "",
};

const UNIDADES = ["UN", "PC", "CX", "FL", "KG", "G", "L", "ML", "M", "M²", "H", "SV"];

export default function BlingProductDialog({ open, onClose, product, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [showDupModal, setShowDupModal] = useState(false);
  const [pendingCreate, setPendingCreate] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(product
        ? { nome: product.nome || "", codigo: product.codigo || "", preco: product.preco || "",
            ncm: product.ncm || "", unidade: product.unidade || "UN", tipo: product.tipo || "S",
            situacao: product.situacao || "A", descricaoCurta: product.descricaoCurta || "" }
        : EMPTY_FORM);
      setDuplicates([]);
      setShowDupModal(false);
    }
  }, [open, product]);

  const handleChange = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleNameBlur = async () => {
    if (!form.nome.trim() || product?.id) return;
    setChecking(true);
    try {
      const { data } = await api.get("/catalog/products/bling/check-duplicate", { params: { nome: form.nome.trim() } });
      if (data.length > 0) {
        setDuplicates(data);
        setShowDupModal(true);
      }
    } catch { /* silencioso */ }
    finally { setChecking(false); }
  };

  const doCreate = async () => {
    setSaving(true);
    try {
      const payload = { ...form, preco: parseFloat(form.preco) || 0 };
      if (product?.id) {
        await api.put(`/catalog/products/bling/${product.id}`, payload);
        toast.success("Produto atualizado no Bling.");
      } else {
        const { data } = await api.post("/catalog/products/bling", payload);
        toast.success(`Produto criado no Bling (ID ${data.id}).`);
      }
      onSaved?.();
      onClose();
    } catch (err) { toastError(err); }
    finally { setSaving(false); setPendingCreate(false); }
  };

  const handleSave = async () => {
    if (!form.nome.trim()) { toast.warn("Informe o nome do produto."); return; }
    if (duplicates.length > 0 && !product?.id && !pendingCreate) {
      setShowDupModal(true);
      return;
    }
    await doCreate();
  };

  const handleLinkExisting = () => {
    setShowDupModal(false);
    onSaved?.({ linked: duplicates[0] });
    onClose();
  };

  const handleCreateAnyway = async () => {
    setShowDupModal(false);
    setPendingCreate(true);
    await doCreate();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{product?.id ? "Editar Produto no Bling" : "Novo Produto no Bling"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Nome *" fullWidth value={form.nome}
                onChange={handleChange("nome")} onBlur={handleNameBlur}
                InputProps={{ endAdornment: checking ? <CircularProgress size={16} /> : null }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Código (SKU)" fullWidth value={form.codigo} onChange={handleChange("codigo")} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Preço (R$)" fullWidth type="number" value={form.preco} onChange={handleChange("preco")} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="NCM" fullWidth value={form.ncm} onChange={handleChange("ncm")} />
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Unidade" fullWidth value={form.unidade} onChange={handleChange("unidade")}>
                {UNIDADES.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Tipo" fullWidth value={form.tipo} onChange={handleChange("tipo")}>
                <MenuItem value="P">Produto</MenuItem>
                <MenuItem value="S">Serviço</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Situação" fullWidth value={form.situacao} onChange={handleChange("situacao")}>
                <MenuItem value="A">Ativo</MenuItem>
                <MenuItem value="I">Inativo</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Descrição curta" fullWidth multiline rows={2}
                value={form.descricaoCurta} onChange={handleChange("descricaoCurta")} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDupModal} onClose={() => setShowDupModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Produto parecido encontrado</DialogTitle>
        <DialogContent>
          {duplicates.map(d => (
            <Alert key={d.id} severity="warning" sx={{ mb: 1 }}>
              <strong>{d.nome}</strong>{d.codigo ? ` (cód: ${d.codigo})` : ""}
            </Alert>
          ))}
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, alignItems: "stretch", p: 2 }}>
          <Button variant="contained" onClick={handleLinkExisting}>Vincular ao existente</Button>
          <Button variant="outlined" onClick={handleCreateAnyway}>Criar mesmo assim</Button>
          <Button onClick={() => setShowDupModal(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
